// Turning an organization into a URL segment, and back again.
//
// This lives apart from the page so it can be executed by a test rather than
// read. The page it serves revokes seats, removes administrators and runs
// reconciliation, so "the URL resolves to the organization it names" is a
// safety property, not a cosmetic one — and a property is only guarded if
// something runs it.

export interface SluggableOrg {
  id: string
  name?: string | null
  remembers_customer_id: string
}

function slugBase(org: SluggableOrg): string {
  const name = (org.name || '').trim().toLowerCase()
  if (!name) return org.remembers_customer_id
  const slug = name.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  // A name of nothing but punctuation slugifies to the empty string, which
  // would produce `/organizations/` — a URL that selects everything and
  // nothing. Fall back to the id, as for a nameless org.
  return slug || org.remembers_customer_id
}

// How much of an id is enough to tell two organizations apart.
//
// A fixed 8 characters is NOT enough, and the test that found this was the one
// that ran the code rather than reading it: two ids sharing a prefix produced
// one slug for two organizations. Ids are unique in full, so widening until the
// group separates always terminates — and `full` is the guaranteed stop.
const SUFFIX_LENGTHS = [8, 12, 18, Number.MAX_SAFE_INTEGER]

function suffixLengthFor(ids: readonly string[]): number {
  for (const length of SUFFIX_LENGTHS) {
    if (new Set(ids.map((id) => id.slice(0, length))).size === ids.length) return length
  }
  return Number.MAX_SAFE_INTEGER
}

/** Every organization's slug, guaranteed distinct within `all`.
 *
 * Uniqueness is established over the whole list at once rather than per
 * organization, because the failure modes are all cross-organization: two orgs
 * sharing a name, two ids sharing a prefix, and a suffixed slug landing on some
 * third organization's plain one. Checking each in isolation cannot see any of
 * them. */
export function slugMap(all: readonly SluggableOrg[]): Map<string, string> {
  const byBase = new Map<string, SluggableOrg[]>()
  for (const org of all) {
    const base = slugBase(org)
    const group = byBase.get(base)
    if (group) group.push(org)
    else byBase.set(base, [org])
  }

  const slugs = new Map<string, string>()
  for (const [base, group] of byBase) {
    if (group.length === 1) {
      slugs.set(group[0].id, base)
      continue
    }
    // A shared name suffixes EVERY member, not all-but-one. Handing the clean
    // slug to whichever happened to sort first would make the plain URL mean
    // different tenants depending on list order.
    const length = suffixLengthFor(group.map((org) => org.id))
    for (const org of group) slugs.set(org.id, `${base}-${org.id.slice(0, length)}`)
  }

  // A suffixed slug can still land on another organization's plain one — an org
  // literally named "Widgets Ltd aaaaaaaa" beside two named "Widgets Ltd". Rare,
  // and silent when it happens, so it is resolved rather than hoped away: the
  // loser takes its full id, which no other organization can hold.
  const taken = new Map<string, string>()
  for (const org of [...all].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))) {
    const slug = slugs.get(org.id)!
    const owner = taken.get(slug)
    if (owner === undefined) {
      taken.set(slug, org.id)
      continue
    }
    slugs.set(org.id, org.id)
    taken.set(org.id, org.id)
  }
  return slugs
}

/** A readable, linkable segment for `org`, unique within `all`.
 *
 * Two organizations may legitimately share a name. A colliding slug would
 * silently open whichever sorted first, so a collision carries an id suffix —
 * and only the organizations that actually collide pay for it. */
export function slugFor(org: SluggableOrg, all: readonly SluggableOrg[]): string {
  // An org is always addressable, even if the caller passes a list without it.
  return slugMap(all.some((item) => item.id === org.id) ? all : [...all, org]).get(org.id)!
}

/** The organization a URL segment names, or `undefined`.
 *
 * `undefined` is a real answer. The caller must show "no organization matches"
 * rather than falling back to the first in the list: a stale or mistyped link
 * that quietly opens somebody else's tenant is the worst version of the
 * autoselect bug, because the address bar names one organization while every
 * panel describes another. */
export function resolveOrg<T extends SluggableOrg>(
  slug: string | undefined,
  all: readonly T[],
): T | undefined {
  if (!slug) return undefined
  let decoded = slug
  try {
    decoded = decodeURIComponent(slug)
  } catch {
    // A malformed escape ("%zz") throws. The raw segment cannot match a slug we
    // generated, so this resolves to nothing — which is the safe answer.
  }
  const slugs = slugMap(all)
  return (
    all.find((org) => slugs.get(org.id) === decoded)
    // Ids stay addressable: links held from before the slugs existed, and
    // anything copied out of the API, keep working after a rename.
    ?? all.find((org) => org.id === decoded)
  )
}
